/**
 * Accordion - Core Component (Enhanced Wrapper)
 *
 * This component wraps shadcn/ui Accordion with simplified type handling
 * and additional customization options.
 *
 * Enhancements over shadcn/ui:
 * - Unified API for single/multiple accordion types
 * - Automatic TypeScript type handling for value props (string vs string[])
 * - Custom icon support in AccordionTrigger (replaces default chevron)
 * - Option to hide the chevron icon entirely
 * - Default collapsible=true for single type
 * - `keepMounted` on AccordionContent: keeps closed content in the HTML so it
 *   stays indexable (see the comment on AccordionContent below)
 *
 * Sub-components:
 * - Accordion: Container with type (single/multiple) control
 * - AccordionItem: Individual collapsible section
 * - AccordionTrigger: Clickable header with optional custom icon
 * - AccordionContent: Collapsible content area
 *
 * @see {@link file://./../ui/accordion.tsx} for the underlying implementation
 */
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { forwardRef, type ComponentRef, type ReactNode } from 'react';
import {
  Accordion as ShadcnAccordion,
  AccordionItem as ShadcnAccordionItem,
  AccordionTrigger as ShadcnAccordionTrigger,
  AccordionContent as ShadcnAccordionContent,
} from '@/app/_components/ui/accordion';
import { cn } from '@/app/_lib/utils';

// Content typography classes from design tokens
const contentTypographyClasses = [
  // Mobile
  'text-accordion-content font-accordion-content tracking-accordion-content font-accordion-content-typeface',
  // Desktop
  'lg:text-accordion-content-lg lg:font-accordion-content-lg lg:tracking-accordion-content-lg lg:font-accordion-content-typeface-lg',
].join(' ');

// Trigger typography classes from design tokens
const triggerTypographyClasses = [
  // Default (Mobile)
  'text-accordion-trigger font-accordion-trigger tracking-accordion-trigger font-accordion-trigger-typeface',
  // Default (Desktop)
  'lg:text-accordion-trigger-lg lg:font-accordion-trigger-lg lg:tracking-accordion-trigger-lg lg:font-accordion-trigger-typeface-lg',
  // Active [data-state=open] (Mobile)
  '[data-state=open]:text-accordion-trigger-active [data-state=open]:font-accordion-trigger-active [data-state=open]:tracking-accordion-trigger-active [data-state=open]:font-accordion-trigger-active-typeface',
  // Active [data-state=open] (Desktop)
  'lg:[data-state=open]:text-accordion-trigger-active-lg lg:[data-state=open]:font-accordion-trigger-active-lg lg:[data-state=open]:tracking-accordion-trigger-active-lg lg:[data-state=open]:font-accordion-trigger-active-typeface-lg',
  // Hover (Mobile)
  'hover:text-accordion-trigger-hover hover:font-accordion-trigger-hover hover:tracking-accordion-trigger-hover hover:font-accordion-trigger-hover-typeface',
  // Hover (Desktop)
  'lg:hover:text-accordion-trigger-hover-lg lg:hover:font-accordion-trigger-hover-lg lg:hover:tracking-accordion-trigger-hover-lg lg:hover:font-accordion-trigger-hover-typeface-lg',
  // Hover + Active (Mobile)
  'hover:[data-state=open]:text-accordion-trigger-hover-active hover:[data-state=open]:font-accordion-trigger-hover-active hover:[data-state=open]:tracking-accordion-trigger-hover-active hover:[data-state=open]:font-accordion-trigger-hover-active-typeface',
  // Hover + Active (Desktop)
  'lg:hover:[data-state=open]:text-accordion-trigger-hover-active-lg lg:hover:[data-state=open]:font-accordion-trigger-hover-active-lg lg:hover:[data-state=open]:tracking-accordion-trigger-hover-active-lg lg:hover:[data-state=open]:font-accordion-trigger-hover-active-typeface-lg',
].join(' ');

export type AccordionType = 'single' | 'multiple';

export interface AccordionProps {
  /**
   * The type of accordion - single allows one item open at a time, multiple allows multiple
   * @default 'single'
   */
  type?: AccordionType;
  /**
   * Whether the accordion should be collapsible (only applies to single type)
   * @default true
   */
  collapsible?: boolean;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Accordion items
   */
  children?: ReactNode;
  /**
   * Default value(s) - string for single, string[] for multiple
   */
  defaultValue?: string | string[];
  /**
   * Controlled value(s) - string for single, string[] for multiple
   */
  value?: string | string[];
  /**
   * Callback when accordion value changes
   */
  onValueChange?: (value: string | string[]) => void;
}

export interface AccordionItemProps {
  /**
   * The value that identifies this accordion item
   */
  value: string;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Item content
   */
  children?: ReactNode;
}

export interface AccordionTriggerProps {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Whether to hide the chevron icon
   * @default false
   */
  hideChevron?: boolean;
  /**
   * Custom icon to replace the default chevron
   */
  icon?: ReactNode;
  /**
   * Trigger content
   */
  children?: ReactNode;
  /**
   * Passive tracking key rendered as data-track-id on the underlying button.
   */
  'data-track-id'?: string;
}

export interface AccordionContentProps {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Content
   */
  children?: ReactNode;
  /**
   * Render the content even while the item is closed, so its text is part of
   * the server HTML and can be indexed. Collapsing is then done in CSS.
   * Use it for editorial content (FAQ answers); leave it off for navigation
   * or anything that only makes sense once opened.
   * @default false
   */
  keepMounted?: boolean;
}

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      type = 'single',
      collapsible = true,
      className,
      children,
      defaultValue,
      value,
      onValueChange,
    },
    ref
  ) => {
    if (type === 'single') {
      return (
        <ShadcnAccordion
          ref={ref}
          type="single"
          collapsible={collapsible}
          className={cn('rounded-accordion-radius', className)}
          defaultValue={defaultValue as string}
          value={value as string}
          onValueChange={onValueChange}
        >
          {children}
        </ShadcnAccordion>
      );
    }

    return (
      <ShadcnAccordion
        ref={ref}
        type="multiple"
        className={cn('rounded-accordion-radius', className)}
        defaultValue={defaultValue as string[]}
        value={value as string[]}
        onValueChange={onValueChange}
      >
        {children}
      </ShadcnAccordion>
    );
  }
);

export const AccordionItem = forwardRef<
  ComponentRef<typeof ShadcnAccordionItem>,
  AccordionItemProps
>(({ className, children, value }, ref) => {
  return (
    <ShadcnAccordionItem ref={ref} value={value} className={cn(className)}>
      {children}
    </ShadcnAccordionItem>
  );
});

export const AccordionTrigger = forwardRef<
  ComponentRef<typeof ShadcnAccordionTrigger>,
  AccordionTriggerProps
>(({ className, hideChevron = false, icon, children, 'data-track-id': dataTrackId }, ref) => {
  if (icon && !hideChevron) {
    return (
      <ShadcnAccordionTrigger
        ref={ref}
        data-track-id={dataTrackId}
        className={cn(
          'rounded-accordion-trigger-radius focus-visible:px-accordion-focus [&>svg]:hidden [&[data-state=open]_.custom-icon]:rotate-180',
          triggerTypographyClasses,
          className
        )}
      >
        <div className="flex flex-1 items-start justify-between gap-4">
          <div className="flex-1">{children}</div>
          <div className="custom-icon pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200">
            {icon}
          </div>
        </div>
      </ShadcnAccordionTrigger>
    );
  }

  return (
    <ShadcnAccordionTrigger
      ref={ref}
      data-track-id={dataTrackId}
      className={cn(
        'rounded-accordion-trigger-radius focus-visible:px-accordion-focus',
        triggerTypographyClasses,
        hideChevron && '[&>svg]:hidden',
        className
      )}
    >
      {children}
    </ShadcnAccordionTrigger>
  );
});

export const AccordionContent = forwardRef<
  ComponentRef<typeof ShadcnAccordionContent>,
  AccordionContentProps
>(({ className, children, keepMounted = false }, ref) => {
  // Radix unmounts closed content, so answer text never reached the HTML and
  // crawlers saw the questions without the answers. `forceMount` keeps it
  // rendered, but Radix then also drops its `hidden` attribute, so the closed
  // state has to be styled by us: see accordion-content-collapse in
  // accordion.token.css. This bypasses the shadcn wrapper on purpose, because
  // that wrapper hardcodes the height keyframes we must not use here (they
  // reset to full height when the animation ends) and the ui/ mirror is
  // off-limits for edits.
  if (keepMounted) {
    return (
      <AccordionPrimitive.Content
        ref={ref}
        forceMount
        data-slot="accordion-content"
        className="text-sm"
      >
        {/* The collapse sits on this inner element, never on Radix' content
            element: Radix writes `transition-duration: 0s` inline there to
            measure the height and, with content kept mounted, never restores
            it. An inline style beats every rule, so a transition placed there
            would silently never run. */}
        <div className="accordion-content-collapse">
          {/* Clips while the row collapses. Must stay padding-free, or the
              padding keeps the row from reaching zero and the panel hangs
              before snapping shut. */}
          <div className="min-h-0 overflow-hidden">
            <div className={cn('pt-0 pb-4', contentTypographyClasses, className)}>
              {children}
            </div>
          </div>
        </div>
      </AccordionPrimitive.Content>
    );
  }

  return (
    <ShadcnAccordionContent ref={ref} className={cn(contentTypographyClasses, className)}>
      {children}
    </ShadcnAccordionContent>
  );
});

Accordion.displayName = 'Accordion';
AccordionItem.displayName = 'AccordionItem';
AccordionTrigger.displayName = 'AccordionTrigger';
AccordionContent.displayName = 'AccordionContent';

export default Accordion;
